import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { csvReceiveStock, CSVReceiveStockRequest } from "@/api/stock-movement-api";
import { useState } from "react";
import { Upload, X, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface BulkStockUploadDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

interface ParsedRow {
    variantId: string;
    sku?: string;
    quantity: number;
    rowNumber: number;
}

export function BulkStockUploadDialog({
    open,
    onOpenChange,
    onSuccess,
}: BulkStockUploadDialogProps) {
    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
    const [parseError, setParseError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleFileSelect = (selectedFile: File) => {
        // Validate file type
        if (!selectedFile.name.endsWith('.csv')) {
            setParseError("Please select a CSV file");
            return;
        }

        setFile(selectedFile);
        setParseError(null);
        parseCSV(selectedFile);
    };

    const parseCSV = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.split('\n').filter(line => line.trim());

                if (lines.length < 2) {
                    setParseError("CSV file is empty or has no data rows");
                    setParsedData([]);
                    return;
                }

                // Parse header
                const header = lines[0].split(',').map(h => h.trim().toLowerCase());
                const variantIdIndex = header.findIndex(h => h === 'variantid' || h === 'variant_id');
                const skuIndex = header.findIndex(h => h === 'sku');
                const quantityIndex = header.findIndex(h => h === 'quantity' || h === 'qty');

                if (variantIdIndex === -1 && skuIndex === -1) {
                    setParseError("CSV must have either 'variantId' or 'sku' column");
                    setParsedData([]);
                    return;
                }

                if (quantityIndex === -1) {
                    setParseError("CSV must have 'quantity' column");
                    setParsedData([]);
                    return;
                }

                // Parse data rows
                const parsed: ParsedRow[] = [];
                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map(v => v.trim());

                    const variantId = variantIdIndex !== -1 ? values[variantIdIndex] : '';
                    const sku = skuIndex !== -1 ? values[skuIndex] : '';
                    const quantity = parseInt(values[quantityIndex]);

                    if ((variantId || sku) && !isNaN(quantity) && quantity > 0) {
                        parsed.push({
                            variantId: variantId || sku,
                            sku: sku || variantId,
                            quantity,
                            rowNumber: i + 1,
                        });
                    }
                }

                if (parsed.length === 0) {
                    setParseError("No valid data rows found in CSV");
                    setParsedData([]);
                    return;
                }

                setParsedData(parsed);
                setParseError(null);
            } catch (error) {
                setParseError("Failed to parse CSV file. Please check the format.");
                setParsedData([]);
            }
        };

        reader.onerror = () => {
            setParseError("Failed to read file");
            setParsedData([]);
        };

        reader.readAsText(file);
    };

    const handleUpload = async () => {
        if (parsedData.length === 0) {
            toast({
                title: "No data to upload",
                description: "Please select a valid CSV file with data",
                variant: "destructive",
            });
            return;
        }

        setUploading(true);
        try {
            const requests: CSVReceiveStockRequest[] = parsedData.map(row => ({
                sku: row.sku || '',
                quantity: row.quantity,
            }));

            const response = await csvReceiveStock(requests);

            if (response.success) {
                toast({
                    title: "Success",
                    description: `Successfully received stock for ${parsedData.length} variant(s)`,
                });

                // Reset state
                setFile(null);
                setParsedData([]);
                setParseError(null);

                // Close dialog and trigger refresh
                onOpenChange(false);
                onSuccess?.();
            } else {
                toast({
                    title: "Upload failed",
                    description: response.message || "Failed to upload stock data",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error("Error uploading stock:", error);
            toast({
                title: "Upload failed",
                description: "An error occurred while uploading stock data",
                variant: "destructive",
            });
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) handleFileSelect(droppedFile);
    };

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(true);
    };

    const onDragLeave = () => setDragOver(false);

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) handleFileSelect(selectedFile);
    };

    const handleClearFile = () => {
        setFile(null);
        setParsedData([]);
        setParseError(null);
    };

    const handleDownloadTemplate = () => {
        const template = "sku,quantity\nC001,10\nC002,25";
        const blob = new Blob([template], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'stock_upload_template.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>Upload Stock CSV</DialogTitle>
                    <DialogDescription>
                        Upload a CSV file to receive stock in bulk. The file should contain variantId (or sku) and quantity columns.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4">
                    {/* Download Template Button */}
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadTemplate}
                            className="gap-2"
                        >
                            <FileText className="h-4 w-4" />
                            Download Template
                        </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        className={cn(
                            "rounded-lg border-2 border-dashed p-8 text-center transition-all duration-200",
                            dragOver
                                ? "border-blue-400 bg-blue-50/50"
                                : "border-slate-300 bg-slate-50/30 hover:border-slate-400"
                        )}
                    >
                        <div
                            className={cn(
                                "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full transition-all",
                                dragOver ? "bg-blue-100" : "bg-white border border-slate-200"
                            )}
                        >
                            <Upload
                                className={cn(
                                    "h-6 w-6 transition-colors",
                                    dragOver ? "text-blue-600" : "text-slate-600"
                                )}
                            />
                        </div>
                        <p className="text-sm font-medium text-slate-700 mb-1">
                            Drag & drop a CSV file here, or{" "}
                            <label className="text-blue-600 hover:text-blue-700 hover:underline cursor-pointer font-semibold">
                                browse
                                <input
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                />
                            </label>
                        </p>
                        <p className="text-xs text-slate-500">
                            CSV file with variantId and quantity columns
                        </p>
                    </div>

                    {/* File Info */}
                    {file && (
                        <div className="rounded-lg border bg-white p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 flex items-center justify-center rounded bg-slate-100">
                                        <FileText className="h-5 w-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium">{file.name}</div>
                                        <div className="text-xs text-slate-500">
                                            {(file.size / 1024).toFixed(2)} KB
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleClearFile}
                                    disabled={uploading}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Parse Error */}
                    {parseError && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{parseError}</AlertDescription>
                        </Alert>
                    )}

                    {/* Success Preview */}
                    {parsedData.length > 0 && !parseError && (
                        <Alert className="border-green-200 bg-green-50">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            <AlertDescription className="text-green-800">
                                Successfully parsed {parsedData.length} row(s) from CSV
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Data Preview Table */}
                    {parsedData.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="max-h-64 overflow-y-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-16">#</TableHead>
                                            <TableHead>Variant ID / SKU</TableHead>
                                            <TableHead className="text-right">Quantity</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {parsedData.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell className="font-medium">{idx + 1}</TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    {row.variantId}
                                                </TableCell>
                                                <TableCell className="text-right">{row.quantity}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={uploading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={parsedData.length === 0 || uploading}
                        className="gap-2"
                    >
                        {uploading ? (
                            <>
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="h-4 w-4" />
                                Upload Stock
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
